import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminInsights } from "@edufadder/shared-types";
import { Chip, colors, fonts, radii, spacing } from "@edufadder/ui";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";
import { EvidenceChip } from "./EvidenceChip.js";

/**
 * Data colours come from the validated reference categorical palette (first three
 * slots, which pass every check including all-pairs on a light surface) — NOT the
 * FSU brand colours, which fail the lightness/chroma checks for data encoding.
 * Garnet and gold stay on the page chrome.
 */
const SERIES = [
  { key: "scan_signed_in", label: "Pages checked (signed in)", color: "#2a78d6" },
  { key: "scan_anonymous", label: "Pages checked (no account)", color: "#eb6834" },
  { key: "passage_check", label: "Website passage checks", color: "#1baf7a" },
] as const;

type SeriesKey = (typeof SERIES)[number]["key"];

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

function niceMax(v: number): number {
  if (v <= 4) return 4;
  const pow = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * pow >= v) return m * pow;
  return 10 * pow;
}

function ActivityChart({ daily }: { daily: AdminInsights["daily"] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const W = 760;
  const H = 220;
  const pad = { l: 40, r: 8, t: 12, b: 26 };
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  const totals = daily.map((d) => SERIES.reduce((n, sr) => n + (d[sr.key as SeriesKey] as number), 0));
  const max = niceMax(Math.max(1, ...totals));
  const band = plotW / daily.length;
  const barW = Math.min(24, band - 2);
  const y = (v: number): number => pad.t + plotH - (v / max) * plotH;
  const ticks = [0, max / 4, max / 2, (3 * max) / 4, max];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: spacing(3), marginBottom: spacing(2) }}>
        <div role="list" aria-label="Legend" style={{ display: "flex", gap: spacing(4), flexWrap: "wrap", fontSize: 13 }}>
          {SERIES.map((sr) => (
            <span role="listitem" key={sr.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: colors.ink }}>
              <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 3, background: sr.color, display: "inline-block" }} />
              {sr.label}
            </span>
          ))}
        </div>
        <button type="button" style={{ ...s.buttonSecondary, padding: "2px 10px", fontSize: 13 }} onClick={() => setShowTable((v) => !v)}>
          {showTable ? "Show chart" : "Show as table"}
        </button>
      </div>
      {showTable ? (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Day</th>
              {SERIES.map((sr) => (
                <th key={sr.key} style={s.th}>
                  {sr.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {daily.map((d) => (
              <tr key={d.day}>
                <td style={s.td}>{d.day}</td>
                {SERIES.map((sr) => (
                  <td key={sr.key} style={s.td}>
                    {d[sr.key as SeriesKey]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ position: "relative", overflowX: "auto" }}>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Stacked daily activity for the last 30 days" style={{ minWidth: 520, display: "block" }}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke={t === 0 ? "#c3c2b7" : "#e1e0d9"} strokeWidth={1} />
                <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill={colors.muted}>
                  {compact(t)}
                </text>
              </g>
            ))}
            {daily.map((d, i) => {
              const cx = pad.l + band * i + band / 2;
              let acc = 0;
              const segs = SERIES.map((sr) => {
                const v = d[sr.key as SeriesKey] as number;
                const seg = { sr, v, y0: acc, y1: acc + v };
                acc += v;
                return seg;
              }).filter((sg) => sg.v > 0);
              return (
                <g key={d.day} opacity={hover === null || hover === i ? 1 : 0.45}>
                  {segs.map((sg, j) => {
                    const top = y(sg.y1);
                    const bottom = y(sg.y0);
                    const isTop = j === segs.length - 1;
                    const h = Math.max(0, bottom - top - (j > 0 ? 2 : 0)); // 2px surface gap between stacked segments
                    const r = isTop ? Math.min(4, h / 2, barW / 2) : 0;
                    const x = cx - barW / 2;
                    const yTop = top;
                    // Rounded data-end on the top segment only; square at the baseline.
                    const path = `M${x},${yTop + h} L${x},${yTop + r} Q${x},${yTop} ${x + r},${yTop} L${x + barW - r},${yTop} Q${x + barW},${yTop} ${x + barW},${yTop + r} L${x + barW},${yTop + h} Z`;
                    return <path key={sg.sr.key} d={path} fill={sg.sr.color} />;
                  })}
                  {(i % 5 === 0 || i === daily.length - 1) && (
                    <text x={cx} y={H - 8} textAnchor="middle" fontSize={11} fill={colors.muted}>
                      {d.day.slice(5)}
                    </text>
                  )}
                  {/* Hit target: the whole column band, taller than the mark. */}
                  <rect
                    x={pad.l + band * i}
                    y={pad.t}
                    width={band}
                    height={plotH}
                    fill="transparent"
                    tabIndex={0}
                    aria-label={`${d.day}: ${SERIES.map((sr) => `${sr.label} ${d[sr.key as SeriesKey]}`).join(", ")}`}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(i)}
                    onBlur={() => setHover(null)}
                  />
                </g>
              );
            })}
          </svg>
          {hover !== null && daily[hover] && (
            <div
              role="tooltip"
              style={{
                position: "absolute",
                top: 4,
                left: `${Math.min(70, Math.max(0, ((pad.l + band * hover) / W) * 100 - 10))}%`,
                background: "#fff",
                border: `1px solid ${colors.border}`,
                borderRadius: radii.md,
                boxShadow: "0 6px 18px rgba(16,24,32,.15)",
                padding: `${spacing(2)} ${spacing(3)}`,
                fontSize: 13,
                pointerEvents: "none",
                minWidth: 200,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{daily[hover].day}</div>
              {SERIES.map((sr) => (
                <div key={sr.key} style={{ display: "flex", justifyContent: "space-between", gap: spacing(3) }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: sr.color }} />
                    {sr.label}
                  </span>
                  <strong>{daily[hover]?.[sr.key as SeriesKey]}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: number | string; sub?: string }): JSX.Element {
  return (
    <div style={{ ...s.card, marginBottom: 0 }}>
      <p style={{ ...s.statLabel, fontSize: 13 }}>{label}</p>
      <p style={{ margin: `${spacing(1)} 0 0`, fontSize: 30, fontWeight: 700, color: colors.ink, fontFamily: fonts.sans }}>
        {typeof value === "number" ? compact(value) : value}
      </p>
      {sub && <p style={{ ...s.mutedText, margin: `${spacing(1)} 0 0` }}>{sub}</p>}
    </div>
  );
}

export function AdminInsightsView(): JSX.Element {
  const [data, setData] = useState<AdminInsights | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.getAdminInsights().then(setData).catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load insights."));
  }, []);

  if (error) return <p style={s.errorText}>{error}</p>;
  if (!data) return <p style={s.mutedText}>Loading insights…</p>;

  const scans = data.scans_signed_in + data.scans_anonymous;
  const outdated = data.extension_versions.filter((v) => !v.is_latest).reduce((n, v) => n + v.scans, 0);
  const versionTotal = data.extension_versions.reduce((n, v) => n + v.scans, 0);
  const maxFlag = Math.max(1, ...data.top_flagged.map((t) => t.times_flagged));

  return (
    <div>
      <p style={{ ...s.mutedText, fontSize: 14, marginTop: 0 }}>
        Last {data.window_days} days. Totals only: these numbers come from anonymous daily counters and aggregate feedback — they
        contain no information about what any individual read, or where.
      </p>

      <div className="ef-grid-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", marginBottom: spacing(6) }}>
        <Tile label="Pages checked" value={scans} sub={`${compact(data.scans_anonymous)} without an account`} />
        <Tile label="Website passage checks" value={data.passage_checks} />
        <Tile label="Accounts" value={data.accounts_total} sub={`${data.accounts_new} new`} />
        <Tile label="Edufads published" value={data.edufads_published} sub={`${data.claims_total} claims in the workbench`} />
        <Tile label="Extension on latest version" value={versionTotal ? `${Math.round(((versionTotal - outdated) / versionTotal) * 100)}%` : "—"} sub={data.latest_extension_version ? `v${data.latest_extension_version}` : undefined} />
      </div>

      <section style={s.card}>
        <h2 style={{ ...s.heading2, color: colors.brand }}>Daily activity</h2>
        <ActivityChart daily={data.daily} />
      </section>

      <section style={s.card}>
        <h2 style={{ ...s.heading2, color: colors.brand }}>Needs attention</h2>
        <ul style={{ margin: 0, paddingLeft: spacing(5), lineHeight: 1.9 }}>
          <li>
            <Link to="/suggestions">{data.suggestions_new} reader suggestion{data.suggestions_new === 1 ? "" : "s"}</Link> waiting for a curator
          </li>
          <li>
            <Link to="/feedback">{data.feedback_new} new feedback item{data.feedback_new === 1 ? "" : "s"}</Link> to triage
          </li>
          <li>
            <Link to="/claims">{data.drafts_awaiting_review} evidence draft{data.drafts_awaiting_review === 1 ? "" : "s"}</Link> waiting for review
          </li>
          <li>{data.unverified_published_sources} source citation{data.unverified_published_sources === 1 ? "" : "s"} in published entries not yet verified by a reviewer</li>
        </ul>
      </section>

      <div className="ef-grid-2" style={{ alignItems: "start" }}>
        <section style={{ ...s.card, marginBottom: 0 }}>
          <h2 style={{ ...s.heading2, color: colors.brand }}>Most-flagged ideas</h2>
          {data.top_flagged.length === 0 ? (
            <p style={s.mutedText}>No flags yet.</p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {data.top_flagged.map((t) => (
                <li key={t.id} style={{ padding: `${spacing(2)} 0`, borderTop: `1px solid ${colors.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: spacing(3) }}>
                    <span style={{ fontWeight: 600 }}>{t.label}</span>
                    <strong>{compact(t.times_flagged)}</strong>
                  </div>
                  <div style={{ height: 8, background: colors.goldTint, borderRadius: 4, margin: `${spacing(1)} 0` }}>
                    <div style={{ width: `${(t.times_flagged / maxFlag) * 100}%`, height: "100%", background: "#2a78d6", borderRadius: "0 4px 4px 0" }} />
                  </div>
                  {t.published ? <EvidenceChip strength={t.evidence_strength} /> : <Chip tone="amber">Not published</Chip>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section style={{ ...s.card, marginBottom: 0 }}>
          <h2 style={{ ...s.heading2, color: colors.brand }}>Feedback signals by idea</h2>
          {data.feedback_signals.length === 0 ? (
            <p style={s.mutedText}>No feedback yet.</p>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Idea</th>
                  <th style={s.th}>Helpful</th>
                  <th style={s.th}>Not helpful</th>
                  <th style={s.th}>Not about it</th>
                  <th style={s.th}>Too broad</th>
                </tr>
              </thead>
              <tbody>
                {data.feedback_signals.map((f) => (
                  <tr key={f.claim_id}>
                    <td style={s.td}>{f.label}</td>
                    <td style={s.td}>{f.helpful}</td>
                    <td style={s.td}>{f.unhelpful}</td>
                    <td style={s.td}>{f.not_about_this}</td>
                    <td style={s.td}>{f.too_broad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ fontSize: 15, margin: `${spacing(6)} 0 ${spacing(2)}` }}>Extension versions in use</h3>
          {data.extension_versions.length === 0 ? (
            <p style={s.mutedText}>No extension checks yet.</p>
          ) : (
            <table style={s.table}>
              <tbody>
                {data.extension_versions.map((v) => (
                  <tr key={v.version}>
                    <td style={s.td}>v{v.version}</td>
                    <td style={s.td}>{compact(v.scans)} checks</td>
                    <td style={s.td}>
                      {v.is_latest ? <Chip tone="green">Latest</Chip> : v.below_minimum ? <Chip tone="red">Below minimum</Chip> : <Chip tone="amber">Update available</Chip>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section style={{ ...s.card, marginTop: spacing(6), background: colors.goldTint, borderLeft: `6px solid ${colors.gold}` }}>
        <h2 style={{ ...s.heading2, color: colors.brand }}>How this makes EduFadder better for the next reader</h2>
        <ul style={{ margin: 0, paddingLeft: spacing(5), lineHeight: 1.7 }}>
          <li><strong>Most-flagged ideas</strong> show where readers actually meet edufads — prioritise keeping those entries current and their sources verified.</li>
          <li><strong>“Not about this idea” and “too broad”</strong> reports point to trigger phrases or aliases that over-match: tighten them, and every installed extension gets the fix within hours via the lexicon — no update needed.</li>
          <li><strong>“Not helpful”</strong> reports flag notes that need clearer wording or newer research — draft a new version; once reviewed and published, every reader sees it.</li>
          <li><strong>Reader suggestions</strong> become new draft entries; publishing one teaches every installed extension to recognise a new idea immediately.</li>
          <li><strong>Extension versions</strong> show who is on an old build: raise <code>EXTENSION_MIN_VERSION</code> on the server to prompt them, or use the release file's feature switches to turn a feature off for everyone at once.</li>
        </ul>
      </section>
    </div>
  );
}
